// PlanningPage - Internal schedule management + Sign attendance
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays, Clock, MapPin, User, Plus, Trash2, Pencil,
  ChevronLeft, ChevronRight, Loader2, CheckCircle2,
  Coffee, BookOpen, PenLine, X, Undo2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`${BACKEND}/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

// ─── Constants ───
const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const LUNCH_START = 12.5;
const LUNCH_END = 14;
const DAY_START = 8;
const HOUR_HEIGHT = 52;

const courseColorPalette = [
  { bg: "linear-gradient(135deg, #6366F1, #818CF8)", light: "#EEF2FF", text: "#4338CA", border: "#A5B4FC" },
  { bg: "linear-gradient(135deg, #8B5CF6, #A78BFA)", light: "#F5F3FF", text: "#6D28D9", border: "#C4B5FD" },
  { bg: "linear-gradient(135deg, #EC4899, #F472B6)", light: "#FDF2F8", text: "#BE185D", border: "#F9A8D4" },
  { bg: "linear-gradient(135deg, #F59E0B, #FBBF24)", light: "#FFFBEB", text: "#B45309", border: "#FDE68A" },
  { bg: "linear-gradient(135deg, #10B981, #34D399)", light: "#ECFDF5", text: "#047857", border: "#6EE7B7" },
  { bg: "linear-gradient(135deg, #3B82F6, #60A5FA)", light: "#EFF6FF", text: "#1D4ED8", border: "#93C5FD" },
  { bg: "linear-gradient(135deg, #EF4444, #F87171)", light: "#FEF2F2", text: "#B91C1C", border: "#FCA5A5" },
  { bg: "linear-gradient(135deg, #14B8A6, #2DD4BF)", light: "#F0FDFA", text: "#0F766E", border: "#5EEAD4" },
  { bg: "linear-gradient(135deg, #F97316, #FB923C)", light: "#FFF7ED", text: "#C2410C", border: "#FDBA74" },
  { bg: "linear-gradient(135deg, #06B6D4, #22D3EE)", light: "#ECFEFF", text: "#0E7490", border: "#67E8F9" },
];

// ═══════════════════ Signature Canvas Popup ═══════════════════
function SignaturePopup({ schedule, workspaceId, onClose, onSigned }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };

  const stopDraw = () => setDrawing(false);

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (!hasDrawn) return toast.error("Veuillez signer avant de valider");
    setSubmitting(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const res = await api("/attendance/sign", {
        method: "POST",
        body: JSON.stringify({
          schedule_id: schedule.schedule_id,
          workspace_id: workspaceId,
          signature,
        }),
      });
      if (res.ok) {
        toast.success("Présence signée avec succès ✓");
        onSigned(schedule.schedule_id);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors de la signature");
      }
    } catch { toast.error("Erreur de connexion"); }
    setSubmitting(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)"
    }} onClick={onClose}>
      <div style={{
        background: "var(--card)", borderRadius: 20, padding: 0, width: 460, maxWidth: "95vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", animation: "popIn 0.2s ease-out"
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #6366F1, #8B5CF6)", padding: "16px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <div style={{ color: "white" }}>
            <div style={{ fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <PenLine style={{ width: 18, height: 18 }} /> Signer ma présence
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {schedule.course_name || schedule.title}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", padding: 6, cursor: "pointer", display: "flex" }}>
            <X style={{ width: 16, height: 16, color: "white" }} />
          </button>
        </div>

        {/* Canvas */}
        <div style={{ padding: 24 }}>
          <p style={{ fontSize: 13, color: "var(--muted-foreground)", marginBottom: 12 }}>
            Signez ci-dessous pour confirmer votre présence :
          </p>
          <div style={{
            border: "2px dashed var(--border)", borderRadius: 12, overflow: "hidden",
            background: "#fafbfc", position: "relative"
          }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={160}
              style={{ width: "100%", height: 160, cursor: "crosshair", touchAction: "none" }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            {!hasDrawn && (
              <div style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                pointerEvents: "none", color: "var(--muted-foreground)", fontSize: 13, opacity: 0.5
              }}>
                ✍️ Dessinez votre signature ici
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
            <Button variant="ghost" size="sm" onClick={clearCanvas} style={{ gap: 4, borderRadius: 20 }}>
              <Undo2 className="h-3.5 w-3.5" /> Effacer
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} style={{ borderRadius: 20 }}>Annuler</Button>
            <Button size="sm" onClick={submit} disabled={!hasDrawn || submitting}
              style={{ gap: 6, borderRadius: 20, background: "linear-gradient(135deg, #10B981, #34D399)", border: "none", color: "white" }}>
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Valider la signature
            </Button>
          </div>
        </div>
      </div>
      <style>{`@keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
    </div>
  );
}


// ═══════════════════ Main PlanningPage ═══════════════════
export default function PlanningPage() {
  const { workspaceId } = useParams();
  const { canManage, user, isAdmin } = useAuth();
  const isCandidate = user?.role === "candidate" || user?.role === "student";

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", trainer_name: "", room: "", start_time: "", end_time: "" });
  const [editingSchedule, setEditingSchedule] = useState(null); // schedule being edited
  const [editData, setEditData] = useState({});
  const [viewMode, setViewMode] = useState("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  // Signature state
  const [signPopup, setSignPopup] = useState(null); // schedule obj
  const [signedIds, setSignedIds] = useState(new Set());

  useEffect(() => { loadData(); }, [workspaceId]);

  // Refresh "now" every 30 seconds for sign button visibility
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const schedRes = await api(`/planning/workspaces/${workspaceId}/schedules`);
      if (schedRes.ok) {
        const data = await schedRes.json();
        setSchedules(data.schedules || []);
      }
      if (isCandidate) {
        try {
          const attRes = await api(`/attendance/my?workspace_id=${workspaceId}`);
          if (attRes.ok) {
            const attData = await attRes.json();
            setSignedIds(new Set((attData.records || []).map(r => r.schedule_id)));
          }
        } catch {}
      }
    } catch (err) {
      console.error("Failed to load:", err);
    }
    setLoading(false);
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.start_time || !formData.end_time) { toast.error("Titre, début et fin requis"); return; }
    setSaving(true);
    try {
      const res = await api(`/planning/workspaces/${workspaceId}/schedules`, {
        method: "POST", body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Séance ajoutée");
        setFormData({ title: "", description: "", trainer_name: "", room: "", start_time: "", end_time: "" });
        setShowForm(false);
        loadData();
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur de connexion"); }
    setSaving(false);
  };

  const openEditForm = (s) => {
    const fmt = (dt) => { try { const d = new Date(dt); const pad = (n) => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; } catch { return ""; } };
    setEditData({ title: s.title || s.course_name || "", description: s.description || "", trainer_name: s.trainer_name || "", room: s.room || "", start_time: fmt(s.start_time), end_time: fmt(s.end_time) });
    setEditingSchedule(s);
  };

  const handleEditSchedule = async (e) => {
    e.preventDefault();
    if (!editData.title || !editData.start_time || !editData.end_time) { toast.error("Titre, début et fin requis"); return; }
    setSaving(true);
    try {
      const res = await api(`/planning/workspaces/${workspaceId}/schedules/${editingSchedule.schedule_id}`, {
        method: "PUT", body: JSON.stringify(editData),
      });
      if (res.ok) { toast.success("Séance modifiée"); setEditingSchedule(null); loadData(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur de connexion"); }
    setSaving(false);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("Supprimer cette séance ?")) return;
    try {
      const res = await api(`/planning/workspaces/${workspaceId}/schedules/${scheduleId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Séance supprimée"); setEditingSchedule(null); loadData(); }
      else toast.error("Erreur");
    } catch { toast.error("Erreur de connexion"); }
  };

  // ─── Helpers ───
  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => { const d = new Date(currentDate); viewMode === "week" ? d.setDate(d.getDate() - 7) : d.setMonth(d.getMonth() - 1); setCurrentDate(d); };
  const goNext = () => { const d = new Date(currentDate); viewMode === "week" ? d.setDate(d.getDate() + 7) : d.setMonth(d.getMonth() + 1); setCurrentDate(d); };

  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  };
  const getMonthDays = (date) => {
    const year = date.getFullYear(), month = date.getMonth();
    const first = new Date(year, month, 1);
    const startDay = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = -startDay; i < 42 - startDay; i++) days.push(new Date(year, month, i + 1));
    return days;
  };

  const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  const isToday = (d) => isSameDay(d, new Date());

  // Multi-day support: a schedule appears on a day if the day is within [start_date, end_date]
  const getSchedulesForDay = (day) => schedules.filter((s) => {
    try {
      const start = new Date(s.start_time);
      const end = new Date(s.end_time);
      const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0);
      const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59);
      // Schedule overlaps with this day if it starts before end of day AND ends after start of day
      return start <= dayEnd && end >= dayStart;
    } catch { return false; }
  }).map((s) => {
    // For multi-day schedules, show the same time slot on each day
    const start = new Date(s.start_time);
    const end = new Date(s.end_time);

    if (isSameDay(start, day) && isSameDay(end, day)) {
      return s; // Single-day schedule, no adjustment needed
    }

    // Multi-day: use the original hours/minutes on this day
    // Format as naive ISO string (no Z suffix) to match original data format
    const pad = (n) => String(n).padStart(2, '0');
    const dayStr = `${day.getFullYear()}-${pad(day.getMonth()+1)}-${pad(day.getDate())}`;
    const startTimeStr = `${pad(start.getHours())}:${pad(start.getMinutes())}:00`;
    const endTimeStr = `${pad(end.getHours())}:${pad(end.getMinutes())}:00`;

    return {
      ...s,
      start_time: `${dayStr}T${startTimeStr}`,
      end_time: `${dayStr}T${endTimeStr}`,
      _multiDay: true
    };
  });

  const formatTime = (dt) => { try { return new Date(dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const formatDate = (dt) => { try { return new Date(dt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); } catch { return ""; } };

  const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const courseColorMap = useMemo(() => {
    const map = {};
    [...new Set(schedules.map((s) => s.course_name || s.title))].forEach((name, i) => { map[name] = courseColorPalette[i % courseColorPalette.length]; });
    return map;
  }, [schedules]);

  const timeToOffset = (dt) => { const d = new Date(dt); return Math.max(0, (d.getHours() + d.getMinutes() / 60 - DAY_START) * HOUR_HEIGHT); };
  const timeToDuration = (start, end) => Math.max(HOUR_HEIGHT * 0.5, ((new Date(end) - new Date(start)) / 3600000) * HOUR_HEIGHT);

  // Check if sign button should show for a schedule
  const canSign = useCallback((s) => {
    if (!isCandidate) return false;
    if (signedIds.has(s.schedule_id)) return false;
    try {
      const start = new Date(s.start_time);
      const windowStart = new Date(start.getTime() - 10 * 60000);
      const windowEnd = new Date(start.getTime() + 20 * 60000);
      return now >= windowStart && now <= windowEnd;
    } catch { return false; }
  }, [now, isCandidate, signedIds]);

  const days = viewMode === "week" ? getWeekDays(currentDate) : getMonthDays(currentDate);
  const headerText = viewMode === "week" ? `${formatDate(days[0])} — ${formatDate(days[6])}` : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  if (loading) {
    return (<WorkspaceLayout><div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></WorkspaceLayout>);
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in" style={{ maxWidth: "100%" }}>

        {/* ═══ Header ═══ */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, display: "flex", alignItems: "center", gap: 12, color: "var(--foreground)" }}>
              <span style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)", padding: "10px", borderRadius: 12, display: "flex" }}>
                <CalendarDays className="h-6 w-6" style={{ color: "white" }} />
              </span>
              Planning des cours
            </h1>
            <p style={{ marginTop: 4, color: "var(--muted-foreground)", fontSize: 14 }}>
              {schedules.length} séance{schedules.length !== 1 && "s"} planifiée{schedules.length !== 1 && "s"}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {canManage && (
              <Button size="sm" onClick={() => setShowForm(!showForm)}
                style={{ gap: 6, borderRadius: 20, fontSize: 13, background: "linear-gradient(135deg, #6366F1, #8B5CF6)", border: "none", color: "white" }}>
                <Plus className="h-3.5 w-3.5" /> Ajouter une séance
              </Button>
            )}
          </div>
        </div>

        {/* ═══ Add Schedule Form ═══ */}
        {showForm && canManage && (
          <Card style={{ marginBottom: 20, border: "1px solid var(--primary)", borderRadius: 16, overflow: "hidden" }}>
            <CardHeader style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)", padding: "16px 24px" }}>
              <CardTitle style={{ color: "white", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                <Plus className="h-5 w-5" /> Ajouter une séance
              </CardTitle>
            </CardHeader>
            <CardContent style={{ padding: 24 }}>
              <form onSubmit={handleCreateSchedule}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Titre *</label>
                    <input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="Ex: Docker & Kubernetes"
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Date/heure début *</label>
                    <input type="datetime-local" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} required
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Date/heure fin *</label>
                    <input type="datetime-local" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} required
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Formateur</label>
                    <input value={formData.trainer_name} onChange={(e) => setFormData({...formData, trainer_name: e.target.value})} placeholder="Nom du formateur"
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Salle / Lieu</label>
                    <input value={formData.room} onChange={(e) => setFormData({...formData, room: e.target.value})} placeholder="Salle A1 / En ligne"
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Description</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Description optionnelle" rows={2}
                      style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)", resize: "vertical" }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
                  <Button type="submit" disabled={saving}
                    style={{ gap: 6, borderRadius: 20, background: "linear-gradient(135deg, #6366F1, #8B5CF6)", border: "none", color: "white" }}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)} style={{ borderRadius: 20 }}>Annuler</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ═══ Empty state ═══ */}
        {schedules.length === 0 && !showForm && (
          <Card style={{ borderRadius: 20, border: "2px dashed var(--border)", marginTop: 24 }}>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}>
              <span style={{ background: "linear-gradient(135deg, #EEF2FF, #F5F3FF)", padding: 20, borderRadius: 20, display: "flex" }}>
                <CalendarDays className="h-12 w-12" style={{ color: "#6366F1" }} />
              </span>
              <h3 style={{ marginTop: 20, fontWeight: 600, fontSize: 18 }}>Aucune séance planifiée</h3>
              <p style={{ marginTop: 8, fontSize: 14, color: "var(--muted-foreground)", textAlign: "center", maxWidth: 420 }}>
                {canManage
                  ? "Cliquez sur « Ajouter une séance » pour créer votre planning."
                  : "Aucune séance n'a encore été ajoutée par le formateur."}
              </p>
            </CardContent>
          </Card>
        )}

        {/* ═══ Calendar ═══ */}
        {schedules.length > 0 && (
          <>
            {/* Controls */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button variant="outline" size="sm" onClick={goPrev} style={{ borderRadius: "50%", width: 36, height: 36, padding: 0 }}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToday} style={{ borderRadius: 20, fontSize: 13, fontWeight: 500 }}>Aujourd'hui</Button>
                <Button variant="outline" size="sm" onClick={goNext} style={{ borderRadius: "50%", width: 36, height: 36, padding: 0 }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span style={{ marginLeft: 8, fontWeight: 700, fontSize: 18, color: "var(--foreground)" }}>{headerText}</span>
              </div>
              <div style={{ display: "flex", borderRadius: 20, overflow: "hidden", border: "1px solid var(--border)" }}>
                {["week", "month"].map((m) => (
                  <button key={m} type="button" onClick={() => setViewMode(m)}
                    style={{ padding: "8px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", transition: "all 0.2s",
                      background: viewMode === m ? "linear-gradient(135deg, #6366F1, #8B5CF6)" : "var(--background)",
                      color: viewMode === m ? "white" : "var(--muted-foreground)" }}>
                    {m === "week" ? "Semaine" : "Mois"}
                  </button>
                ))}
              </div>
            </div>

            {/* ═══ WEEK VIEW ═══ */}
            {viewMode === "week" && (
              <div style={{ background: "var(--card)", borderRadius: 16, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "64px repeat(7, 1fr)", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ padding: 12, borderRight: "1px solid var(--border)" }} />
                  {days.map((day, i) => {
                    const today = isToday(day);
                    return (
                      <div key={i} style={{ textAlign: "center", padding: "12px 4px", borderRight: i < 6 ? "1px solid var(--border)" : "none",
                        background: today ? "linear-gradient(135deg, #6366F1, #8B5CF6)" : "transparent" }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1,
                          color: today ? "rgba(255,255,255,0.8)" : "var(--muted-foreground)" }}>{dayNames[i]}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2, color: today ? "white" : "var(--foreground)" }}>{day.getDate()}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "64px repeat(7, 1fr)", position: "relative" }}>
                  <div style={{ borderRight: "1px solid var(--border)" }}>
                    {HOURS.map((h) => (
                      <div key={h} style={{ height: HOUR_HEIGHT, display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
                        paddingRight: 10, fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", position: "relative", top: -6 }}>
                        {h}:00
                      </div>
                    ))}
                  </div>
                  {days.map((day, dayIdx) => {
                    const daySched = getSchedulesForDay(day);
                    return (
                      <div key={dayIdx} style={{ position: "relative", borderRight: dayIdx < 6 ? "1px solid var(--border)" : "none", height: HOURS.length * HOUR_HEIGHT }}>
                        {HOURS.map((h) => (
                          <div key={h} style={{ position: "absolute", top: (h - DAY_START) * HOUR_HEIGHT, left: 0, right: 0,
                            borderTop: "1px solid var(--border)", height: HOUR_HEIGHT, background: h % 2 === 0 ? "transparent" : "rgba(0,0,0,0.01)" }} />
                        ))}
                        {/* Lunch */}
                        <div style={{ position: "absolute", top: (LUNCH_START - DAY_START) * HOUR_HEIGHT, left: 4, right: 4,
                          height: (LUNCH_END - LUNCH_START) * HOUR_HEIGHT,
                          background: "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(245,158,11,0.06) 5px, rgba(245,158,11,0.06) 10px)",
                          border: "1px dashed rgba(245,158,11,0.35)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                          zIndex: 1, pointerEvents: "none" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: "rgba(180,130,40,0.7)",
                            background: "rgba(255,255,255,0.85)", padding: "3px 10px", borderRadius: 12 }}>
                            <Coffee style={{ width: 12, height: 12 }} /> Pause 12h30–14h
                          </span>
                        </div>
                        {/* Course blocks */}
                        {daySched.map((s, j) => {
                          const colors = courseColorMap[s.course_name || s.title] || courseColorPalette[0];
                          const top = timeToOffset(s.start_time);
                          const height = timeToDuration(s.start_time, s.end_time);
                          const showSign = canSign(s);
                          const isSigned = signedIds.has(s.schedule_id);
                          const canDelete = canManage;
                          return (
                            <div key={j} style={{
                              position: "absolute", top: top + 1, left: 5, right: 5,
                              minHeight: Math.max(height - 2, 50),
                              background: colors.bg, borderRadius: 10, padding: "8px 10px", color: "white",
                              overflow: "visible",
                              zIndex: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", cursor: "default", transition: "transform 0.2s, box-shadow 0.2s",
                              display: "flex", flexDirection: "column", justifyContent: "flex-start",
                            }}
                            title={`${s.title || s.course_name}\n${formatTime(s.start_time)} - ${formatTime(s.end_time)}${s.trainer_name ? '\nFormateur: ' + s.trainer_name : ''}${s.room ? '\nSalle: ' + s.room : ''}`}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)"; e.currentTarget.style.zIndex = 10; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"; e.currentTarget.style.zIndex = 2; }}>
                              <div style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.3 }}>
                                {s.course_name || s.title}
                              </div>
                              <div style={{ fontSize: 10, opacity: 0.95, marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                <Clock style={{ width: 10, height: 10 }} />
                                {formatTime(s.start_time)} – {formatTime(s.end_time)}
                              </div>
                              {s.trainer_name && (
                                <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                  <User style={{ width: 10, height: 10 }} /> {s.trainer_name}
                                </div>
                              )}
                              {s.room && (
                                <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                  <MapPin style={{ width: 10, height: 10 }} /> {s.room}
                                </div>
                              )}
                              {/* Sign / Signed badge */}
                              {isSigned && (
                                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 800,
                                  background: "rgba(255,255,255,0.3)", padding: "4px 12px", borderRadius: 10, width: "fit-content",
                                  border: "1px solid rgba(255,255,255,0.4)" }}>
                                  <CheckCircle2 style={{ width: 13, height: 13 }} /> Signé ✓
                                </div>
                              )}
                              {showSign && !isSigned && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); setSignPopup(s); }}
                                  style={{
                                    marginTop: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800,
                                    background: "#16a34a", color: "white", padding: "6px 14px", borderRadius: 10,
                                    border: "2px solid rgba(255,255,255,0.6)", cursor: "pointer", width: "fit-content",
                                    animation: "pulseGlow 1.5s infinite", letterSpacing: 0.3,
                                    boxShadow: "0 2px 10px rgba(22,163,74,0.5)"
                                  }}>
                                  <PenLine style={{ width: 14, height: 14 }} /> ✍️ SIGNER
                                </button>
                              )}
                              {canDelete && (
                                <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 2 }}>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); openEditForm(s); }}
                                    style={{ background: "rgba(0,0,0,0.3)", border: "none", borderRadius: "50%", padding: 4, cursor: "pointer", display: "flex", opacity: 0.6, transition: "opacity 0.2s" }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0.6}
                                    title="Modifier cette séance">
                                    <Pencil style={{ width: 12, height: 12, color: "white" }} />
                                  </button>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(s.schedule_id); }}
                                    style={{ background: "rgba(0,0,0,0.3)", border: "none", borderRadius: "50%", padding: 4, cursor: "pointer", display: "flex", opacity: 0.6, transition: "opacity 0.2s" }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0.6}
                                    title="Supprimer cette séance">
                                    <Trash2 style={{ width: 12, height: 12, color: "white" }} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ MONTH VIEW ═══ */}
            {viewMode === "month" && (
              <div style={{ background: "var(--card)", borderRadius: 16, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                  {dayNames.map((d) => (
                    <div key={d} style={{ textAlign: "center", padding: "12px 4px", fontSize: 11, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)", borderBottom: "1px solid var(--border)", background: "var(--muted)" }}>{d}</div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                  {days.map((day, i) => {
                    const daySched = getSchedulesForDay(day);
                    const today = isToday(day);
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    return (
                      <div key={i} style={{ minHeight: 110, padding: 6, borderRight: (i + 1) % 7 !== 0 ? "1px solid var(--border)" : "none",
                        borderBottom: "1px solid var(--border)", opacity: isCurrentMonth ? 1 : 0.35, background: today ? "rgba(99,102,241,0.04)" : "transparent" }}>
                        <div style={{ fontSize: 13, fontWeight: today ? 800 : 500, marginBottom: 4,
                          ...(today ? { background: "linear-gradient(135deg, #6366F1, #8B5CF6)", color: "white", borderRadius: "50%", width: 28, height: 28,
                            display: "flex", alignItems: "center", justifyContent: "center" } : { color: "var(--muted-foreground)" }) }}>
                          {day.getDate()}
                        </div>
                        {daySched.slice(0, 3).map((s, j) => {
                          const colors = courseColorMap[s.course_name || s.title] || courseColorPalette[0];
                          return (
                            <div key={j} style={{ fontSize: 10, borderRadius: 6, padding: "2px 6px", marginBottom: 2,
                              background: colors.bg, color: "white", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                              title={`${s.title} ${formatTime(s.start_time)}-${formatTime(s.end_time)}${s.trainer_name ? '\nFormateur: ' + s.trainer_name : ''}${s.room ? '\nSalle: ' + s.room : ''}`}>
                              {formatTime(s.start_time)} {s.course_name || s.title}{s.trainer_name ? ` · ${s.trainer_name}` : ''}{s.room ? ` · ${s.room}` : ''}
                            </div>
                          );
                        })}
                        {daySched.length > 3 && (
                          <p style={{ fontSize: 10, color: "var(--muted-foreground)", textAlign: "center", marginTop: 2 }}>+{daySched.length - 3}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legend */}
            {schedules.length > 0 && (
              <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", padding: "12px 20px", background: "var(--muted)", borderRadius: 12 }}>
                <BookOpen style={{ width: 14, height: 14, color: "var(--muted-foreground)" }} />
                {Object.entries(courseColorMap).map(([name, colors]) => (
                  <span key={name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--foreground)", fontWeight: 500 }}>
                    <span style={{ width: 14, height: 14, borderRadius: 4, display: "inline-block", background: colors.bg }} />
                    {name}
                  </span>
                ))}
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted-foreground)" }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, display: "inline-block",
                    background: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(245,158,11,0.15) 3px, rgba(245,158,11,0.15) 6px)",
                    border: "1px dashed rgba(245,158,11,0.5)" }} />
                  Pause déjeuner
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Schedule Popup */}
      {editingSchedule && canManage && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} onClick={() => setEditingSchedule(null)}>
          <div style={{ background: "var(--card)", borderRadius: 20, width: 520, maxWidth: "95vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            overflow: "hidden", animation: "popIn 0.2s ease-out" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24)", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Pencil style={{ width: 18, height: 18 }} /> Modifier la séance
              </div>
              <button type="button" onClick={() => setEditingSchedule(null)}
                style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", padding: 6, cursor: "pointer", display: "flex" }}>
                <X style={{ width: 16, height: 16, color: "white" }} />
              </button>
            </div>
            <form onSubmit={handleEditSchedule} style={{ padding: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Titre *</label>
                  <input value={editData.title} onChange={(e) => setEditData({...editData, title: e.target.value})} required
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Début *</label>
                  <input type="datetime-local" value={editData.start_time} onChange={(e) => setEditData({...editData, start_time: e.target.value})} required
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Fin *</label>
                  <input type="datetime-local" value={editData.end_time} onChange={(e) => setEditData({...editData, end_time: e.target.value})} required
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Formateur</label>
                  <input value={editData.trainer_name} onChange={(e) => setEditData({...editData, trainer_name: e.target.value})}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Salle / Lieu</label>
                  <input value={editData.room} onChange={(e) => setEditData({...editData, room: e.target.value})}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Description</label>
                  <textarea value={editData.description} onChange={(e) => setEditData({...editData, description: e.target.value})} rows={2}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)", resize: "vertical" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)", justifyContent: "space-between" }}>
                <Button type="button" variant="ghost" onClick={() => handleDeleteSchedule(editingSchedule.schedule_id)}
                  style={{ gap: 6, borderRadius: 20, color: "#B91C1C" }}>
                  <Trash2 className="h-4 w-4" /> Supprimer
                </Button>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button type="button" variant="ghost" onClick={() => setEditingSchedule(null)} style={{ borderRadius: 20 }}>Annuler</Button>
                  <Button type="submit" disabled={saving}
                    style={{ gap: 6, borderRadius: 20, background: "linear-gradient(135deg, #F59E0B, #FBBF24)", border: "none", color: "white" }}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} Enregistrer
                  </Button>
                </div>
              </div>
            </form>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}

      {/* Signature Popup */}
      {signPopup && (
        <SignaturePopup
          schedule={signPopup}
          workspaceId={workspaceId}
          onClose={() => setSignPopup(null)}
          onSigned={(id) => setSignedIds(prev => new Set([...prev, id]))}
        />
      )}

      <style>{`
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.4); }
          50% { box-shadow: 0 0 8px 3px rgba(22,163,74,0.3); }
        }
      `}</style>
    </WorkspaceLayout>
  );
}
